import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { ApiError } from '@/api/client';
import i18n from '@/i18n';

type Severity = 'success' | 'error' | 'info' | 'warning';
interface Message {
  id: number;
  text: string;
  severity: Severity;
}

/**
 * App-wide feedback for what just happened — "Saved", or why a request failed.
 *
 * A module-level channel rather than context alone, so code outside the React
 * tree (the query client's mutation cache) can report through it too. Messages
 * queue: a second toast waits for the first rather than replacing it unread.
 */
type Listener = (message: Message) => void;
const listeners = new Set<Listener>();
let nextId = 1;

function emit(text: string, severity: Severity) {
  if (!text) return;
  const message = { id: nextId++, text, severity };
  for (const listener of listeners) listener(message);
}

/** Callers pass translated text. */
export const toast = {
  success: (text: string) => emit(text, 'success'),
  error: (text: string) => emit(text, 'error'),
  info: (text: string) => emit(text, 'info'),
  warning: (text: string) => emit(text, 'warning'),
};

/**
 * What to tell a person about a failed request: the stable error code's
 * wording when the API sent one (so Arabic does not depend on the server's
 * locale), else the message itself.
 */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    // A code nobody has worded yet would show as `leave.overlap`; the server's sentence beats that.
    return err.code && i18n.exists(err.code) ? i18n.t(err.code) : i18n.t(err.message);
  }
  if (err instanceof Error) return i18n.t(err.message);
  return String(err);
}

export function ToastProvider({ children }: { children?: ReactNode }) {
  const [queue, setQueue] = useState<Message[]>([]);
  const [open, setOpen] = useState(false);
  const current = queue[0];

  useEffect(() => {
    const listener: Listener = (message) =>
      // The same text twice in a row (a retried request) is one toast, not two.
      setQueue((prev) => (prev.some((m) => m.text === message.text) ? prev : [...prev, message]));
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (current) setOpen(true);
  }, [current]);

  return (
    <>
      {children}
      <Snackbar
        key={current?.id}
        open={open && Boolean(current)}
        autoHideDuration={current?.severity === 'error' ? 8000 : 4000}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') setOpen(false);
        }}
        TransitionProps={{ onExited: () => setQueue((prev) => prev.slice(1)) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {current ? (
          <Alert
            severity={current.severity}
            variant="filled"
            onClose={() => setOpen(false)}
            sx={{ width: '100%', whiteSpace: 'pre-line' }}
          >
            {current.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
